import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { BlockOutlined, ErrorOutline, FileDownloadOutlined } from "@mui/icons-material";
import { DataGrid, GridColDef, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useParams } from "react-router-dom";

import { useAuth } from "contexts/auth";
import api from "services/api";
import { Page } from "v2/components";
import { errorMessageOf } from "v2/utils/errorMessageOf";
import { bateBusca, DOCUMENTO_COLUMNS, estadoDe } from "./documentos-columns";
import { IDocumentoColaborador, IDocumentosResposta, LIMITE_ZIP } from "./types";

const FALHA_DOWNLOAD = "Não foi possível baixar o documento. Tente novamente.";
const FALHA_INVALIDAR = "Não foi possível invalidar a versão. Tente novamente.";
const SEM_ACESSO = "Você não tem acesso aos documentos desta empresa.";
const AVISO_FILA =
  'Se o arquivo não chegou, os documentos podem ter saído de "Ainda não baixados"; desligue o filtro para baixar de novo.';
const reserva = (error: any, padrao: string) => (error?.response?.status === 403 ? SEM_ACESSO : padrao);
// why: o registro de entrega é gravado antes dos bytes saírem; só recusa 4xx garante que nada foi registrado.
const podeTerSaidoDaFila = (error: any) => !error?.response || error.response.status >= 500;

const lerTexto = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });

/** Com responseType "blob" o corpo do erro também vem como Blob: lê o texto e faz o parse. */
async function lerErro(error: any, padrao: string = FALHA_DOWNLOAD): Promise<{ code?: string; message: string }> {
  // 5xx ou sem resposta: a mensagem do servidor é técnica (SQL, timeout) e não vai para a tela.
  if (podeTerSaidoDaFila(error)) return { message: padrao };
  const body = error?.response?.data;
  if (body instanceof Blob) {
    try {
      const json = JSON.parse(await lerTexto(body));
      return { code: json?.code ?? json?.data?.code, message: json?.data?.message || json?.message || reserva(error, padrao) };
    } catch {
      return { message: reserva(error, padrao) };
    }
  }
  return { code: body?.code ?? body?.data?.code, message: errorMessageOf(error, reserva(error, padrao)) };
}

function salvarArquivo(resp: any, nomePadrao: string) {
  const cd: string = resp.headers?.["content-disposition"] ?? "";
  const nome = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd)?.[1];
  const url = URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.download = nome ? decodeURIComponent(nome) : nomePadrao;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

type Executar = (motivo?: string) => Promise<void>;

export function DocumentosTab({ organizationId, contratoInicial, cpfInicial }: { organizationId?: string; contratoInicial?: string; cpfInicial?: string } = {}) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const orgId = organizationId ?? auth.user?.organization?.id;
  // A mesma flag que a ListContracts usa para separar corretora de empresa.
  const ehCorretora = !!auth.user?.isSeller;

  const [busca, setBusca] = useState(cpfInicial ?? "");
  const [soPendentes, setSoPendentes] = useState(ehCorretora);
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [erroDownload, setErroDownload] = useState<string | null>(null);
  const [pedirMotivo, setPedirMotivo] = useState<Executar | null>(null);
  // O mesmo diálogo serve ao download (motivo só para a Equipe Conducco) e ao invalidar (motivo sempre).
  const [acaoDoMotivo, setAcaoDoMotivo] = useState<"baixar" | "invalidar">("baixar");
  const [executando, setExecutando] = useState(false);
  const [motivo, setMotivo] = useState("");

  const { data, isFetching, isFetched, isError, error } = useQuery({
    queryKey: ["collaboratorDocumentos", orgId, contratoInicial ?? null],
    queryFn: () =>
      api.get<IDocumentosResposta>(`/v2/cdp/collaborators/${orgId}/documentos`, {
        params: contratoInicial ? { contrato: contratoInicial } : undefined,
      }),
    select: (resp) => resp.data,
    enabled: !!orgId,
  });

  const linhas = useMemo(
    () => (data?.data ?? []).filter((d) => (!soPendentes || d.pendenteParaCorretora) && bateBusca(d, busca)),
    [data, soPendentes, busca],
  );

  // Só o que está na tela e baixa entra no zip: filtro ou recarga que escondem uma linha a tiram da seleção.
  const idsVisiveis = useMemo(() => new Set(linhas.filter((d) => d.versaoVigente).map((d) => d.id)), [linhas]);
  const idsSelecionados = useMemo(() => selecionados.filter((id) => idsVisiveis.has(id)), [selecionados, idsVisiveis]);
  useEffect(() => {
    if (idsSelecionados.length !== selecionados.length) setSelecionados(idsSelecionados);
  }, [idsSelecionados, selecionados]);
  // Modelo da DataGrid 8: { type, ids: Set }.
  const modeloSelecao = useMemo<GridRowSelectionModel>(
    () => ({ type: "include", ids: new Set<GridRowId>(idsSelecionados) }),
    [idsSelecionados],
  );
  const aoMudarSelecao = (m: GridRowSelectionModel) =>
    // "exclude" = todas as linhas selecionáveis visíveis menos as listadas.
    setSelecionados(m.type === "exclude" ? [...idsVisiveis].filter((id) => !m.ids.has(id)) : [...m.ids].map(String));

  const ocultosPeloFiltro = soPendentes
    ? (data?.data ?? []).filter((d) => !d.pendenteParaCorretora && bateBusca(d, busca)).length
    : 0;

  const fecharMotivo = () => {
    setPedirMotivo(null);
    setMotivo("");
    setAcaoDoMotivo("baixar");
  };

  const recarregar = () => queryClient.invalidateQueries({ queryKey: ["collaboratorDocumentos", orgId] });

  // Equipe Conducco sem motivo recebe 400 MOTIVO_OBRIGATORIO: abre o diálogo e reenvia com o motivo.
  const rodar = async (executar: Executar, motivoInformado?: string) => {
    // why: o diálogo é partilhado; o texto de erro e o aviso da fila são da ação, não do diálogo.
    const invalidando = acaoDoMotivo === "invalidar";
    setErroDownload(null);
    setExecutando(true);
    try {
      await executar(motivoInformado);
      fecharMotivo();
      await recarregar();
    } catch (e) {
      const { code, message } = await lerErro(e, invalidando ? FALHA_INVALIDAR : FALHA_DOWNLOAD);
      if (code === "MOTIVO_OBRIGATORIO") {
        setPedirMotivo(() => executar);
        return;
      }
      fecharMotivo();
      setErroDownload(!invalidando && podeTerSaidoDaFila(e) ? `${message} ${AVISO_FILA}` : message);
      // A versão pode ter sido superada (409): a lista precisa refletir.
      await recarregar();
    } finally {
      setExecutando(false);
    }
  };

  const baixarUm = (doc: IDocumentoColaborador): Executar => async (m) => {
    const resp = await api.get(`/v2/cdp/collaborators/${orgId}/documentos/${doc.id}/download`, {
      params: m ? { motivo: m } : undefined,
      responseType: "blob",
    });
    salvarArquivo(resp, `${doc.tipo}_${doc.cpf}`);
  };

  /** Invalidar uma versão (spec Repositório 4.2): só no Cofre, sempre com motivo; nada é apagado. */
  const invalidar = (doc: IDocumentoColaborador): Executar => async (m) => {
    await api.post(`/v2/cdp/collaborators/${orgId}/documentos/${doc.id}/invalidar`, { motivo: m });
  };
  const pedirInvalidacao = (doc: IDocumentoColaborador) => {
    setAcaoDoMotivo("invalidar");
    setPedirMotivo(() => invalidar(doc));
  };

  const baixarZip: Executar = async (m) => {
    const resp = await api.post(
      `/v2/cdp/collaborators/${orgId}/documentos/download-zip`,
      { ids: idsSelecionados, ...(m ? { motivo: m } : {}) },
      { responseType: "blob" },
    );
    salvarArquivo(resp, "documentos.zip");
    setSelecionados([]);
  };

  const columns: GridColDef[] = [
    ...DOCUMENTO_COLUMNS.map((c) => ({
      field: c.field,
      headerName: c.header,
      width: c.width,
      flex: c.flex,
      minWidth: c.minWidth,
      valueGetter: (_v: unknown, row: IDocumentoColaborador) => c.format(row),
    })),
    {
      field: "acoes",
      headerName: "",
      width: 110,
      sortable: false,
      renderCell: (p) => {
        if (estadoDe(p.row) === "INVALIDADA") {
          return (
            <Tooltip title={p.row.motivoInvalidacao ?? "Versão invalidada"}>
              <Typography variant="caption" color="textSecondary" noWrap>
                Invalidada
              </Typography>
            </Tooltip>
          );
        }
        if (!p.row.versaoVigente) {
          return (
            <Tooltip title="Há uma versão mais nova deste documento">
              <Typography variant="caption" color="textSecondary" noWrap>
                Superado
              </Typography>
            </Tooltip>
          );
        }
        return (
          <>
            <Tooltip title="Baixar">
              <IconButton aria-label={`Baixar ${p.row.tipo} de ${p.row.nome ?? p.row.cpf}`} onClick={() => rodar(baixarUm(p.row))}>
                <FileDownloadOutlined />
              </IconButton>
            </Tooltip>
            {p.row.id.startsWith("cofre:") && (
              <Tooltip title="Invalidar esta versão (fica registrado com o motivo)">
                <IconButton aria-label={`Invalidar ${p.row.tipo} de ${p.row.nome ?? p.row.cpf}`} onClick={() => pedirInvalidacao(p.row)}>
                  <BlockOutlined />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  if (!isFetched && isFetching) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={30} thickness={4} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="400px" gap={3} textAlign="center">
        <ErrorOutline sx={{ fontSize: 40, color: "error.main" }} />
        <Typography variant="h6" color="textPrimary">
          Ops! Algo deu errado
        </Typography>
        <Typography variant="body1" color="textSecondary" maxWidth={400}>
          {errorMessageOf(error, reserva(error, "Não foi possível carregar os documentos no momento. Por favor, tente novamente."))}
        </Typography>
      </Box>
    );
  }

  const acimaDoLimite = idsSelecionados.length > LIMITE_ZIP;

  return (
    <>
      {data?.campanhasIndisponiveis && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Documentos de campanha indisponíveis no momento
        </Alert>
      )}
      {erroDownload && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroDownload(null)}>
          {erroDownload}
        </Alert>
      )}
      <Box display="flex" alignItems="center" gap={2} mb={2} flexWrap="wrap">
        <TextField
          size="small"
          label="Buscar por nome ou CPF"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControlLabel
          control={<Switch checked={soPendentes} onChange={(e) => setSoPendentes(e.target.checked)} />}
          label="Ainda não baixados"
        />
        {ocultosPeloFiltro > 0 && (
          <Typography variant="caption" color="textSecondary">
            {ocultosPeloFiltro} documento(s) oculto(s) pelo filtro "Ainda não baixados".
          </Typography>
        )}
      </Box>
      <Box sx={{ width: "100%", height: 600 }}>
        <DataGrid
          rows={linhas}
          columns={columns}
          checkboxSelection
          isRowSelectable={(p) => p.row.versaoVigente}
          rowSelectionModel={modeloSelecao}
          onRowSelectionModelChange={aoMudarSelecao}
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50, 100]}
          localeText={{
            ...ptBR.components.MuiDataGrid.defaultProps.localeText,
            noRowsLabel: "Nenhum documento encontrado",
            // why: o locale ptBR da grade deixa o rodapé em inglês ("1–4 of 4").
            paginationDisplayedRows: ({ from, to, count }) => `${from}–${to} de ${count}`,
          }}
          sx={{ border: 0, backgroundColor: "white" }}
        />
      </Box>
      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={2} mt={2}>
        {idsSelecionados.length > 0 && (
          <Typography variant="caption" color="textSecondary">
            {idsSelecionados.length} {idsSelecionados.length === 1 ? "selecionado" : "selecionados"}
          </Typography>
        )}
        {acimaDoLimite && (
          <Typography variant="caption" color="textSecondary">
            Até {LIMITE_ZIP} documentos por vez
          </Typography>
        )}
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlined />}
          onClick={() => rodar(baixarZip)}
          disabled={!idsSelecionados.length || acimaDoLimite}
          sx={{ textTransform: "none", borderRadius: "8px" }}
        >
          Baixar selecionados (.zip)
        </Button>
      </Box>

      <Dialog open={!!pedirMotivo} onClose={fecharMotivo} fullWidth maxWidth="sm">
        <DialogTitle>{acaoDoMotivo === "invalidar" ? "Invalidar versão" : "Motivo do download"}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" mb={2}>
            {acaoDoMotivo === "invalidar"
              ? "A versão deixa de valer para a aprovação em todos os contratos da empresa. Nada é apagado; o motivo fica registrado."
              : "Este download opera dado pessoal e fica registrado com o motivo informado."}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label={acaoDoMotivo === "invalidar" ? "Motivo da invalidação" : "Motivo do download"}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={fecharMotivo}>Cancelar</Button>
          <Button variant="contained" disabled={!motivo.trim() || executando} onClick={() => pedirMotivo && rodar(pedirMotivo, motivo.trim())}>
            {acaoDoMotivo === "invalidar" ? "Invalidar" : "Baixar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * Página da rota `/documentos/:organizationId?contrato=` (link do e-mail de exportação e da linha
 * "Falta documento"). O CPF vem no estado da rota, nunca na URL: dado pessoal não vai para o
 * histórico do navegador nem para o log do servidor.
 */
export function DocumentosPage() {
  const { organizationId } = useParams() as { organizationId: string };
  const location = useLocation<{ cpf?: string } | undefined>();
  const contrato = new URLSearchParams(location.search).get("contrato") ?? undefined;
  const cpf = location.state?.cpf ?? undefined;
  return (
    <Page title="Documentos">
      <Page.Tab name="Documentos">
        <DocumentosTab organizationId={organizationId} contratoInicial={contrato} cpfInicial={cpf} />
      </Page.Tab>
    </Page>
  );
}
